/**
 * Error Boundary Test Component
 * Used for testing error boundaries in development
 */

import React, { useState } from 'react';
import { Button, Box, Typography, Alert } from '@mui/material';
import { BugReport, Error as ErrorIcon } from '@mui/icons-material';

interface ErrorTestProps {
  show?: boolean;
}

export const ErrorBoundaryTester: React.FC<ErrorTestProps> = ({ show = false }) => {
  const [shouldThrow, setShouldThrow] = useState(false);

  // Only show in development mode
  if (!import.meta.env.DEV || !show) {
    return null;
  }

  if (shouldThrow) {
    throw new Error('Test error from ErrorBoundaryTester component - this is intentional for testing error boundaries');
  }

  const triggerError = () => {
    setShouldThrow(true);
  };

  const triggerAsyncError = async () => {
    // This will be caught by the global error handling system once we set up global handlers
    setTimeout(() => {
      throw new Error('Test async error - this should be handled by the global error handler');
    }, 100);
  };

  const triggerPromiseRejection = () => {
    // Test unhandled promise rejection
    Promise.reject(new Error('Test unhandled promise rejection - this should be handled by the global error handler'));
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px dashed orange', borderRadius: 1 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Development Tools:</strong> Error boundary testing tools
        </Typography>
      </Alert>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<ErrorIcon />}
          onClick={triggerError}
        >
          Test Error Boundary
        </Button>
        
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<BugReport />}
          onClick={triggerAsyncError}
        >
          Test Async Error
        </Button>

        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<BugReport />}
          onClick={triggerPromiseRejection}
        >
          Test Promise Rejection
        </Button>
      </Box>
      
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        <strong>Error Boundary Test:</strong> Triggers a React component error (caught by PageErrorBoundary)<br/>
        <strong>Async Error Test:</strong> Triggers an async JavaScript error (caught by GlobalErrorHandler)<br/>
        <strong>Promise Rejection Test:</strong> Triggers an unhandled promise rejection (caught by GlobalErrorHandler)
      </Typography>
    </Box>
  );
};

export default ErrorBoundaryTester;
